import type { DesignItem } from '../types';
import type { SourceRect } from './types';
import { composeCanvasFilter } from './cssFilters';
import { normalizeImageTransform } from './normalize';
import { resolveImageSourceUrls } from './resolveSource';
import { loadImageBitmap } from '../imageLoader';

export { loadImageBitmap };

export function sourceRectFromTransform(
  transform: ReturnType<typeof normalizeImageTransform>,
  imgW: number,
  imgH: number,
): SourceRect {
  const sw = Math.max(1, imgW);
  const sh = Math.max(1, imgH);
  const { crop } = transform;
  return {
    x: crop.x * sw,
    y: crop.y * sh,
    width: crop.width * sw,
    height: crop.height * sh,
  };
}

/**
 * Draw an image item through the unified pipeline onto a 2D context.
 */
export async function drawImageItem(
  ctx: CanvasRenderingContext2D,
  item: DesignItem,
  destW: number,
  destH: number,
  resolveUrl: (raw: string) => string = (raw) => raw,
): Promise<boolean> {
  const transform = normalizeImageTransform(item);
  const filter = composeCanvasFilter(transform);
  const candidates = resolveImageSourceUrls(item).map(resolveUrl).filter(Boolean);

  for (const abs of candidates) {
    try {
      const img = await loadImageBitmap(abs);
      const iw = Math.max(1, img.naturalWidth || img.width);
      const ih = Math.max(1, img.naturalHeight || img.height);
      const src = sourceRectFromTransform(transform, iw, ih);

      ctx.save();
      if (filter && filter !== 'none') {
        ctx.filter = filter;
      }
      ctx.drawImage(
        img,
        src.x,
        src.y,
        src.width,
        src.height,
        0,
        0,
        destW,
        destH,
      );
      ctx.restore();
      return true;
    } catch {
      // try next URL
    }
  }
  return false;
}
